/* eslint-disable max-lines */
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { useCallback, useRef } from 'react';
import { CanvasDimensions, Point } from './types';
import {
  calculateMidpoints,
  constrainPointToBounds,
  insertVertexAtMidpoint,
  movePolygon,
  pointsToFlatArray,
} from './utils';
import { VERTEX_SIZE } from './VertexHandle';

interface UseZoneDragHandlersProps {
  points: Point[];
  bounds: CanvasDimensions;
  onPointsChange: (points: Point[]) => void;
  groupRef: React.RefObject<Konva.Group>;
  lineRef: React.RefObject<Konva.Line>;
}

export function useZoneDragHandlers({
  points,
  bounds,
  onPointsChange,
  groupRef,
  lineRef,
}: UseZoneDragHandlersProps) {
  const dragPointsRef = useRef<Point[]>(points);

  const updateShapesDirectly = useCallback(
    (newPoints: Point[]) => {
      if (!groupRef.current) return;
      if (lineRef.current) lineRef.current.points(pointsToFlatArray(newPoints));
      const newMidpoints = calculateMidpoints(newPoints);
      groupRef.current.find('Circle').forEach((circle, i) => {
        if (newMidpoints[i]) circle.position(newMidpoints[i]);
      });
      groupRef.current.getLayer()?.batchDraw();
    },
    [groupRef, lineRef]
  );

  const handleDragStart = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      dragPointsRef.current = [...points];
    },
    [points]
  );

  const handleVertexDragMove = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const target = e.target as Konva.Node;
      const index = target.getAttr('data-index') as number;
      const rawPosition = {
        x: target.x() + VERTEX_SIZE / 2,
        y: target.y() + VERTEX_SIZE / 2,
      };
      const newPosition = constrainPointToBounds(rawPosition, bounds);
      const newPoints = [...dragPointsRef.current];
      newPoints[index] = newPosition;
      dragPointsRef.current = newPoints;
      updateShapesDirectly(newPoints);
      target.position({
        x: newPosition.x - VERTEX_SIZE / 2,
        y: newPosition.y - VERTEX_SIZE / 2,
      });
    },
    [bounds, updateShapesDirectly]
  );

  const handleVertexDragEnd = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      onPointsChange([...dragPointsRef.current]);
    },
    [onPointsChange]
  );

  const handleVertexDoubleClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (points.length <= 4) return; // Keep at least 4 vertices
      const target = e.target as Konva.Node;
      const index = target.getAttr('data-index') as number;
      const newPoints = points.filter((_, i) => i !== index);
      onPointsChange(newPoints);
    },
    [points, onPointsChange]
  );

  const handleMidpointDragMove = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const target = e.target as Konva.Node;
      const index = target.getAttr('data-index') as number;
      const newPosition = constrainPointToBounds(
        { x: target.x(), y: target.y() },
        bounds
      );
      const newPoints = insertVertexAtMidpoint(
        dragPointsRef.current,
        index,
        newPosition
      );
      if (lineRef.current) {
        lineRef.current.points(pointsToFlatArray(newPoints));
        lineRef.current.getLayer()?.batchDraw();
      }
    },
    [bounds, lineRef]
  );

  const handleMidpointDragEnd = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const target = e.target as Konva.Node;
      const index = target.getAttr('data-index') as number;
      const newPosition = constrainPointToBounds(
        { x: target.x(), y: target.y() },
        bounds
      );
      onPointsChange(
        insertVertexAtMidpoint(dragPointsRef.current, index, newPosition)
      );
    },
    [bounds, onPointsChange]
  );

  const handlePolygonDragEnd = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      const group = e.target as Konva.Node;
      const deltaX = group.x();
      const deltaY = group.y();
      if (deltaX === 0 && deltaY === 0) return;
      onPointsChange(movePolygon(points, deltaX, deltaY, bounds));
      group.position({ x: 0, y: 0 });
    },
    [points, bounds, onPointsChange]
  );

  return {
    handleDragStart,
    handleVertexDragMove,
    handleVertexDragEnd,
    handleVertexDoubleClick,
    handleMidpointDragMove,
    handleMidpointDragEnd,
    handlePolygonDragEnd,
  };
}
